import { Controller } from "@hotwired/stimulus"

// Signature live speed chart for the Connection capture card.
//
// A single metric (speed) at high fidelity: a smooth area+line trace that
// scrolls right-to-left via a CSS keyframe, a glowing "now" cursor that rides
// the trace at the leading edge, and a digital km/h readout phase-locked to the
// same loop. The trace shape is a synthetic lap "fingerprint" — illustrative
// motion until real per-sample speed is streamed over ActionCable. The y-axis
// range is fixed to a representative 96–330 km/h band.
//
// Built in JS (not server-rendered) so the geometry and the rAF readout share
// one source of truth. Degrades to a static frame if rAF never fires.
export default class extends Controller {
  static targets = ["plot", "readout"]
  static values = {
    accent: { type: String, default: "#A78BFA" },
    height: { type: Number, default: 116 },
    durSec: { type: Number, default: 16 },
    // When false, the trace is drawn static and the km/h readout stays blank —
    // a non-live session must not look like it's actively driving.
    live: { type: Boolean, default: false },
  }

  // Lap fingerprint: [apex position, speed drop 0..1, corner width].
  CORNERS = [
    [3.5, 0.58, 1.5],
    [8.0, 0.3, 1.1],
    [13.5, 0.72, 0.95],
    [18.5, 0.22, 1.4],
    [23.0, 0.46, 1.05],
    [27.5, 0.34, 1.2],
  ]
  PERIOD = 31
  VMIN = 96
  VMAX = 330
  CURSOR_FX = 0.88

  connect() {
    this.uid = `sc${Math.round(this.element.getBoundingClientRect().top)}_${this.PERIOD}`
    this.t0 = performance.now()
    this.render()
    // Only stream a live readout + scrolling cursor while actually capturing.
    if (this.liveValue)
      this.raf = requestAnimationFrame((now) => this.tick(now))
  }

  disconnect() {
    if (this.raf) cancelAnimationFrame(this.raf)
  }

  profile(x) {
    let v = 1
    for (const [c, depth, w] of this.CORNERS) {
      const k = (((x - c) % this.PERIOD) + this.PERIOD) % this.PERIOD
      const d = Math.min(k, this.PERIOD - k)
      v -= depth * Math.exp(-(d * d) / (2 * w * w))
    }
    return Math.max(0.05, Math.min(1, v))
  }

  kmh(v) {
    return Math.round(this.VMIN + v * (this.VMAX - this.VMIN))
  }

  render() {
    const accent = this.accentValue
    const H = this.heightValue
    const VW = 600
    const top = 16
    const bot = H - 12
    const TW = 2 * VW
    const N = 260
    const yV = (v) => bot - v * (bot - top)
    const trackOf = (i) => (i / N) * 2 * this.PERIOD
    const xOf = (i) => (i / N) * TW

    this.yV = yV

    let line = ""
    let area = `M0 ${bot}`
    for (let i = 0; i <= N; i++) {
      const X = xOf(i).toFixed(1)
      const Y = yV(this.profile(trackOf(i))).toFixed(1)
      line += `${i ? "L" : "M"}${X} ${Y} `
      area += ` L${X} ${Y}`
    }
    area += ` L${TW} ${bot} Z`

    const grid = [0.28, 0.56, 0.84]
    const gridLines = grid
      .map(
        (g) =>
          `<line x1="0" x2="${TW}" y1="${yV(g)}" y2="${yV(g)}" stroke="#ffffff" stroke-opacity="0.045" stroke-width="1" vector-effect="non-scaling-stroke" />`,
      )
      .join("")

    this.plotTarget.innerHTML = `
      <svg width="200%" height="${H}" viewBox="0 0 ${TW} ${H}" preserveAspectRatio="none" style="display:block">
        <defs>
          <linearGradient id="${this.uid}f" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${accent}" stop-opacity="0.32" />
            <stop offset="60%" stop-color="${accent}" stop-opacity="0.07" />
            <stop offset="100%" stop-color="${accent}" stop-opacity="0" />
          </linearGradient>
        </defs>
        ${gridLines}
        <line x1="0" x2="${TW}" y1="${bot}" y2="${bot}" stroke="#ffffff" stroke-opacity="0.08" stroke-width="1" vector-effect="non-scaling-stroke" />
        <g class="${this.liveValue ? "trace-scroll" : ""}" style="animation-duration:${this.durSecValue}s">
          <path d="${area}" fill="url(#${this.uid}f)" />
          <path d="${line}" fill="none" stroke="${accent}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" style="filter:drop-shadow(0 0 6px ${accent}cc)" />
        </g>
      </svg>
      ${
        this.liveValue
          ? `
      <div class="pointer-events-none absolute inset-y-0 z-10" style="left:${this.CURSOR_FX * 100}%">
        <div class="absolute inset-y-0 w-px" style="background:linear-gradient(${accent}00, ${accent}66, ${accent}00)"></div>
        <div data-speed-chart-cursor class="absolute h-2.5 w-2.5 -translate-x-1/2 rounded-full" style="left:0; top:50%; background:#fff; box-shadow:0 0 0 3px ${accent}55, 0 0 10px ${accent}"></div>
      </div>`
          : ""
      }
      <div class="pointer-events-none absolute inset-y-0 right-0 z-0 w-10" style="background:linear-gradient(90deg, transparent, var(--color-app-hud-bg))"></div>
    `
    this.cursor = this.plotTarget.querySelector("[data-speed-chart-cursor]")
    if (this.hasReadoutTarget) {
      // Blank when idle — a fabricated km/h on a parked session is misleading.
      this.readoutTarget.textContent = this.liveValue
        ? this.kmh(this.profile(this.CURSOR_FX * this.PERIOD))
        : "—"
    }
  }

  tick(now) {
    const frac = ((now - this.t0) / 1000 / this.durSecValue) % 1
    const track = (this.CURSOR_FX + frac) * this.PERIOD
    const v = this.profile(track)
    if (this.cursor) this.cursor.style.top = `${this.yV(v) - 5}px`
    if (this.hasReadoutTarget) this.readoutTarget.textContent = this.kmh(v)
    this.raf = requestAnimationFrame((t) => this.tick(t))
  }
}
